import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import './DailyJournal.css';

const DailyJournal = ({ userId }) => {
  const [entries, setEntries] = useState([]);
  const [currentEntry, setCurrentEntry] = useState('');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [prompt, setPrompt] = useState('');

  const reflectionPrompts = [
    "What did today's scroll teach you about yourself?",
    "How did you apply today's principles in your life?",
    "What challenged you today, and how did you overcome it?",
    "What are you grateful for today?",
    "What's one thing you'll do differently tomorrow?",
    "How did reading your scroll make you feel today?",
    "What progress did you notice in your personal growth?",
    "What insight resonated with you most today?"
  ];

  useEffect(() => {
    if (userId) {
      loadEntry(selectedDate);
      setRandomPrompt();
    }
  }, [userId, selectedDate]);

  const setRandomPrompt = () => {
    const randomIndex = Math.floor(Math.random() * reflectionPrompts.length);
    setPrompt(reflectionPrompts[randomIndex]);
  };

  const loadEntry = async (date) => {
    try {
      setLoading(true);

      const { data, error } = await supabase
        .from('journal_entries')
        .select('*')
        .eq('user_id', userId)
        .eq('date', date)
        .single();

      if (error && error.code !== 'PGRST116') { // PGRST116 is "no rows found"
        throw error;
      }

      setCurrentEntry(data?.content || '');
      setLoading(false);
    } catch (error) {
      console.error('Error loading journal entry:', error);
      setLoading(false);
    }
  };

  const loadRecentEntries = async () => {
    try {
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

      const { data, error } = await supabase
        .from('journal_entries')
        .select('date, content')
        .eq('user_id', userId)
        .gte('date', thirtyDaysAgo.toISOString().split('T')[0])
        .order('date', { ascending: false })
        .limit(30);

      if (error) throw error;

      setEntries(data || []);
    } catch (error) {
      console.error('Error loading recent entries:', error);
    }
  };

  const saveEntry = async () => {
    if (!currentEntry.trim()) {
      return;
    }

    try {
      setSaving(true);

      // Check if entry exists
      const { data: existingEntry } = await supabase
        .from('journal_entries')
        .select('id')
        .eq('user_id', userId)
        .eq('date', selectedDate)
        .single();

      if (existingEntry) {
        // Update existing entry
        const { error } = await supabase
          .from('journal_entries')
          .update({
            content: currentEntry,
            updated_at: new Date().toISOString()
          })
          .eq('id', existingEntry.id);

        if (error) throw error;
      } else {
        // Insert new entry
        const { error } = await supabase
          .from('journal_entries')
          .insert({
            user_id: userId,
            date: selectedDate,
            content: currentEntry,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          });

        if (error) throw error;
      }

      setSaving(false);
      loadRecentEntries();
      
      // Show success message
      showToast('Journal entry saved!');
    } catch (error) {
      console.error('Error saving journal entry:', error);
      setSaving(false);
      showToast('Error saving entry. Please try again.', 'error');
    }
  };

  const showToast = (message, type = 'success') => {
    // Simple toast implementation - you can replace with your preferred toast library
    const toast = document.createElement('div');
    toast.className = `toast toast-${type}`;
    toast.textContent = message;
    document.body.appendChild(toast);
    
    setTimeout(() => {
      toast.classList.add('show');
    }, 100);

    setTimeout(() => {
      toast.classList.remove('show');
      setTimeout(() => toast.remove(), 300);
    }, 3000);
  };

  const changeDate = (days) => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + days);
    setSelectedDate(newDate.toISOString().split('T')[0]);
  };

  const goToToday = () => {
    setSelectedDate(new Date().toISOString().split('T')[0]);
  };

  const isToday = selectedDate === new Date().toISOString().split('T')[0];
  const isFuture = new Date(selectedDate) > new Date();

  const formatDate = (dateString) => {
    const date = new Date(dateString + 'T00:00:00');
    return date.toLocaleDateString('en-US', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const getCharacterCount = () => {
    return currentEntry.length;
  };

  const getWordCount = () => {
    return currentEntry.trim() ? currentEntry.trim().split(/\s+/).length : 0;
  };

  if (loading) {
    return (
      <div className="daily-journal loading">
        <div className="loading-spinner"></div>
        <p>Loading your journal...</p>
      </div>
    );
  }

  return (
    <div className="daily-journal">
      <div className="journal-header">
        <div className="date-navigation">
          <button onClick={() => changeDate(-1)} className="nav-arrow">←</button>
          <div className="date-display">
            <h2>{formatDate(selectedDate)}</h2>
            {!isToday && (
              <button onClick={goToToday} className="today-link">Go to Today</button>
            )}
          </div>
          <button 
            onClick={() => changeDate(1)} 
            className="nav-arrow"
            disabled={isFuture}
          >
            →
          </button>
        </div>
      </div>

      <div className="journal-prompt">
        <div className="prompt-icon">💭</div>
        <div className="prompt-text">
          <strong>Reflection prompt:</strong> {prompt}
        </div>
        <button onClick={setRandomPrompt} className="refresh-prompt">🔄</button>
      </div>

      <div className="journal-editor">
        <textarea
          value={currentEntry}
          onChange={(e) => setCurrentEntry(e.target.value)}
          placeholder="Write your thoughts, reflections, or insights here..."
          className="journal-textarea"
          disabled={isFuture}
        />
        
        <div className="editor-footer">
          <div className="word-count">
            {getWordCount()} words • {getCharacterCount()} characters
          </div>
          <button 
            onClick={saveEntry}
            disabled={!currentEntry.trim() || saving || isFuture}
            className="save-button"
          >
            {saving ? 'Saving...' : 'Save Entry'}
          </button>
        </div>
      </div>

      {entries.length > 0 && (
        <div className="recent-entries">
          <h3>Recent Entries</h3>
          <div className="entries-list">
            {entries.map((entry) => (
              <div 
                key={entry.date}
                className={`entry-preview ${entry.date === selectedDate ? 'active' : ''}`}
                onClick={() => setSelectedDate(entry.date)}
              >
                <div className="entry-date">
                  {new Date(entry.date + 'T00:00:00').toLocaleDateString('en-US', { 
                    month: 'short', 
                    day: 'numeric' 
                  })}
                </div>
                <div className="entry-snippet">
                  {entry.content.substring(0, 100)}
                  {entry.content.length > 100 ? '...' : ''}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default DailyJournal;
